<script setup>
import DataTableField from '@/components/form/DataTableField.vue'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import DropDownSingleSelect from '@/components/form/DropDownSingleSelect.vue'
import CheckBoxField from '@/components/form/CheckBoxField.vue'
import { actionMenuMixin } from '@/mixins/actionMenuMixin'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

const route = useRoute()
const store = useStore()

const props = defineProps({
  items: { type: Array, required: true },
  count: { type: Number, require: true },
  editable: { require: false, default: true }
})

const { x, y, showMenu, selectedItem, show } = actionMenuMixin()
const { dirtyItems, addDirty, getDirtyClass } = dirtyFieldMixin()

const HEADER = [
  {
    title: 'Reference point',
    value: '',
    field: false,
    class: 'pl-1',
    width: '250px'
  },
  {
    title: 'Permeability aquifer [m/day]',
    value: 'permeability_aquifer',
    field: true,
    class: 'pl-1'
  },
  {
    title: 'Aquifer thickness [m]',
    value: 'aquifer_thickness',
    field: true,
    class: 'pl-1'
  },
  {
    title: 'Foreshore length (L1) [m]',
    value: 'foreshore_length',
    field: true,
    class: 'pl-1'
  },
  {
    title: 'Foreshore leakage length [m]',
    value: 'foreshore_resistance',
    field: true,
    class: 'pl-1'
  },
  {
    title: 'Hinterland length (L3) [m])',
    value: 'hinterland_length',
    field: true,
    class: 'pl-1'
  },
  {
    title: 'Hinterland leakage length [m]',
    value: 'hinterland_resistance',
    field: true,
    class: 'pl-1'
  },
  {
    title: 'Radial outflow resistance (wu)',
    value: 'radial_outflow_resistance',
    checkbox: true,
    field: true,
    class: 'pl-1'
  }
]
const textFieldHeaders = HEADER.filter((header) => header.field)

const itemPerPage = ref(10)
const page = ref(1)
const referencePoints = computed(() => store.state.referencePoint.items)
const loading = computed(() => store.state.burstingParameter.loading)
const availableReferencePoints = computed(
  () => store.state.burstingParameter.availableReferencePoints
)

const pageItems = computed(() => {
  let startIndex = itemPerPage.value * (page.value - 1)
  let endIndex = itemPerPage.value * page.value
  return props.items ? props.items.slice(startIndex, endIndex) : []
})

const addNewItem = async () => {
  await store.dispatch('burstingParameter/addNewItem')
}
const deleteItem = async (id) => {
  await store.dispatch('burstingParameter/deleteItem', id)
}

const create = async () => {
  await store.dispatch('burstingParameter/create')
}
const setParameter = async (input) => {
  await store.dispatch('burstingParameter/setParameter', input)
}

const update = async () => {
  await store.dispatch('burstingParameter/update')
}

const emptyDirtyItems = () => {
  store.dispatch('burstingParameter/emptyDirtyItems')
}

const toggleWriteLoading = () => {
  store.dispatch('burstingParameter/toggleWriteLoading')
}

const addAvailableReferencePoint = (id) => {
  store.dispatch('burstingParameter/addAvailableReferencePoint', id)
}

const editAvailableItemsReferencePoint = async (event) => {
  await store.dispatch('burstingParameter/editAvailableItemsReferencePoint', event)
}

const updateOptions = async (options) => {
  const currentPage = props.items.length / options.itemsPerPage
  if (currentPage && options.page > currentPage) {
    emit('changePage')
  }
  page.value = options.page
}
const borderBottom = (item) => {
  return !item.reference_point ? 'border-bottom-red' : getDirtyClass(item, item.id)
}
const setReferencePoint = (item, event) => {
  editAvailableItemsReferencePoint(event)
  setParameter({ item, value: event['newId'], parameter: 'reference_point' })
}
const saveChanges = async () => {
  try {
    await update()
    await create()
    emptyDirtyItems()
    dirtyItems.value = []
  } catch (e) {
    console.log(e)
    toggleWriteLoading()
  }
}
const add = async () => {
  await addNewItem()
}
const deleteObj = async (id) => {
  await deleteItem(id)
  addAvailableReferencePoint(id)
}
const referencePointsOptions = computed(() => {
  const points = referencePoints.value ? referencePoints.value['features'] : []
  points.forEach((ref) => {
    const index = availableReferencePoints.value.findIndex((usedRef) => ref.id === usedRef)
    ref.disabled = index < 0
  })
  points.sort((a, b) => a.disabled - b.disabled)
  return points
})

const emit = defineEmits(['changePage'])

defineExpose({
  add,
  saveChanges
})
</script>

<template>
  <div>
    <v-data-table-server
      class="data-table"
      :items-length="count ?? 0"
      :items="pageItems"
      :headers="HEADER"
      :loading="loading"
      density="compact"
      :items-per-page="itemPerPage"
      @update:options="updateOptions"
    >
      <template #loader="{ isActive }">
        <v-progress-linear :active="isActive" indeterminate color="primary" />
      </template>
      <template v-slot:item="{ item }">
        <tr @contextmenu="show($event, item)">
          <td>
            <DropDownSingleSelect
              :options="referencePointsOptions"
              :class="`${borderBottom(item)}`"
              :item="item.reference_point"
              @addDirty="addDirty(item, item.id)"
              @update="(e) => setReferencePoint(item, e)"
              :editable="editable"
            />
          </td>

          <td v-for="header in textFieldHeaders" :key="header.value">
            <DataTableField
              v-if="!header.checkbox"
              :class="getDirtyClass(item, header.value)"
              :item="item"
              :parameter="header.value"
              :editable="editable"
              width="75px"
              @set="setParameter"
              @update="addDirty(item, header.value)"
            />
            <CheckBoxField
              v-else
              class="checkbox-field"
              @update="addDirty(item, header.value)"
              :dirtyClass="getDirtyClass(item, header.value)"
              :editable="editable"
              @set="setParameter"
              :item="item"
              :parameter="header.value"
            />
          </td>
        </tr>
      </template>
    </v-data-table-server>
    <TableActionMenu
      v-if="editable"
      :add-button="false"
      :selected-item="selectedItem"
      :show-menu="showMenu"
      :x="x"
      @update:show-menu="showMenu = $event"
      :y="y"
      @action="deleteObj(selectedItem.id)"
    />
  </div>
</template>
<style scoped>
.data-table :deep(.v-data-footer__select) {
  visibility: hidden;
}

.v-table:deep(th) {
  text-align: left;
  padding: 0 !important;
  font-size: 0.75rem;
  font-weight: bold !important;
  color: rgba(0, 0, 0, 0.6);
}
.v-table:deep(td) {
  padding: 0 !important;
}

.checkbox-field :deep(.v-selection-control) {
  min-height: 32px;
}
</style>
